/**
 * Merchant theme resolver (Wave 1, Phase 4 — unified merchant theming)
 *
 * ONE brand source (`workspace.emailStyles`) now drives every surface:
 * emails (resolveEmailStyles, unchanged), popups (resolvePopupStyles idiom),
 * the chat widget, and the suitability ask cards. This module produces the
 * ON-SITE projection: a CSS custom-property map in both vocabularies the SDK
 * uses — the After Hours widget tokens (--gold/--ink/--surface/…) and the ask
 * card's design tokens (--m-accent/--m-ink/--m-surface/…).
 *
 * Contract:
 *  - A workspace on default styling returns null → the SDK keeps the After
 *    Hours look (the brand default; per JP: merchant styling applies to
 *    everything once it is CHANGED from the default).
 *  - Contrast is validated at resolve time: an accent that fails WCAG 4.5:1
 *    against the resolved surface REJECTS the theme (null + warning) rather
 *    than silently rendering an unreadable card (design contract 05).
 *  - Resolved server-side per request (generate-script config, widget_config)
 *    so styling changes apply without re-pasting the embed — the same
 *    property the popup path established.
 *
 * @module @marva/shared/theme-resolver
 */

'use strict';

const { resolveEmailStyles } = require('./email-composer/style-resolver');

// ── Color math ─────────────────────────────────────────────────────────────

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function relativeLuminance({ r, g, b }) {
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two hex colors (1–21). 0 when unparsable. */
function contrastRatio(hexA, hexB) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return 0;
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

function mix(hex, withHex, weight) {
  const a = hexToRgb(hex);
  const b = hexToRgb(withHex);
  if (!a || !b) return hex;
  const ch = (x, y) => Math.round(x * (1 - weight) + y * weight);
  return '#' + [ch(a.r, b.r), ch(a.g, b.g), ch(a.b, b.b)]
    .map(v => v.toString(16).padStart(2, '0')).join('');
}

const lighten = (hex, w) => mix(hex, '#ffffff', w);
const darken = (hex, w) => mix(hex, '#000000', w);

function rgba(hex, alpha) {
  const c = hexToRgb(hex);
  if (!c) return hex;
  return `rgba(${c.r},${c.g},${c.b},${alpha})`;
}

// ── Resolution ─────────────────────────────────────────────────────────────

// Contrast gates, calibrated to how each token is CONSUMED (sweep 2026-08-03:
// a blanket 4.5:1 on the accent rejected Marva's own Daylight gold and two
// shipped presets — silently, while the editor said "saved successfully"):
//  - ink is body text on surface → WCAG text minimum 4.5:1
//  - accent is a button/orb/chip BACKGROUND with its own ink → WCAG non-text
//    (UI component) minimum 3:1
//  - accent-AS-TEXT is handled by deriving an accent-ink shade that actually
//    reaches 4.5:1 (deriveAccentInk), not by rejecting the theme.
const MIN_TEXT_CONTRAST = 4.5;
const MIN_UI_CONTRAST = 3.0;
// Back-compat export (previous name for the single blanket threshold).
const MIN_ACCENT_CONTRAST = MIN_UI_CONTRAST;

/**
 * Derive a readable accent-tinted TEXT color for the given surface: walk the
 * accent toward the surface's opposite pole until it clears 4.5:1. Falls back
 * to the ink color when even a heavy shift can't get there (keeps the brand
 * hue whenever possible, but readability wins).
 */
function deriveAccentInk(accent, surface, ink) {
  const surfaceIsLight = relativeLuminance(hexToRgb(surface) || { r: 255, g: 255, b: 255 }) > 0.5;
  const shift = surfaceIsLight ? darken : lighten;
  for (let w = 0.12; w <= 0.72; w += 0.06) {
    const candidate = shift(accent, w);
    if (contrastRatio(candidate, surface) >= MIN_TEXT_CONTRAST) return candidate;
  }
  return ink;
}

/**
 * Resolve a workspace's on-site theme custom-property map.
 * @returns {Object|null} property map, or null (keep After Hours defaults)
 */
function resolveOnSiteTheme(workspace) {
  const emailStyles = workspace?.emailStyles;
  // Default styling → After Hours stays; nothing to resolve.
  if (!emailStyles) {
    return null;
  }

  const resolved = resolveEmailStyles(emailStyles);
  const accent = resolved.primaryColor;
  const ink = resolved.textColor;
  const soft = resolved.secondaryTextColor;
  const surface = resolved.contentBackgroundColor || '#ffffff';
  const bg = resolved.backgroundColor;

  // Design contract 05: reject failing contrast, never render it — with each
  // pair judged by its actual use (text 4.5:1, UI component 3:1).
  const accentContrast = contrastRatio(accent, surface);
  const inkContrast = contrastRatio(ink, surface);
  if (accentContrast < MIN_UI_CONTRAST || inkContrast < MIN_TEXT_CONTRAST) {
    console.warn('resolveOnSiteTheme: rejected — contrast below minimums', {
      workspaceId: workspace?.id,
      accentContrast: `${accentContrast.toFixed(2)} (min ${MIN_UI_CONTRAST})`,
      inkContrast: `${inkContrast.toFixed(2)} (min ${MIN_TEXT_CONTRAST})`
    });
    return null;
  }

  const accentInk = deriveAccentInk(accent, surface, ink);

  const line = mix(ink, surface, 0.82);
  const raise = mix(surface, ink, 0.04);
  const chipRadius = resolved.buttonBorderRadius || '8px';
  const font = resolved.fontFamily;

  return {
    // ── Ask card vocabulary (--m-*) ──
    '--m-bg': bg,
    '--m-surface': surface,
    '--m-raise': raise,
    '--m-line': line,
    '--m-ink': ink,
    '--m-soft': soft,
    '--m-faint': mix(soft, surface, 0.35),
    '--m-accent': accent,
    '--m-accent-ink': accentInk,
    '--m-accent-hi': lighten(accent, 0.35),
    '--m-accent-lo': darken(accent, 0.45),
    '--m-accent-line': rgba(accent, 0.3),
    '--m-accent-tint': rgba(accent, 0.1),
    '--m-glow': rgba(accent, 0.4),
    // --m-radius deliberately NOT projected: cardBorderRadius is an email
    // default (no merchant input feeds it), and projecting it silently
    // shifted ask cards from their 14px design radius to the email 8px even
    // when the merchant never touched corners. Chip radius does follow the
    // merchant's button radius.
    '--m-chip-radius': chipRadius,
    '--m-font': font,
    '--m-shadow': '0 18px 40px -28px rgba(0,0,0,.35)',

    // ── After Hours widget vocabulary (chat surfaces) ──
    '--bg': bg,
    '--bg-2': mix(bg, ink, 0.03),
    '--surface': surface,
    '--raise': raise,
    '--line': line,
    '--line-soft': mix(line, surface, 0.4),
    '--ink': ink,
    '--ink-soft': soft,
    '--ink-faint': mix(soft, surface, 0.35),
    '--gold': accent,
    '--gold-soft': lighten(accent, 0.18),
    '--gold-ink': accentInk,
    '--gold-tint': rgba(accent, 0.1),
    '--gold-line': rgba(accent, 0.28),
    '--glow': rgba(accent, 0.16),
    '--orb-hi': lighten(accent, 0.35),
    '--orb-mid': accent,
    '--orb-lo': darken(accent, 0.45)
  };
}

module.exports = {
  resolveOnSiteTheme,
  contrastRatio,
  MIN_ACCENT_CONTRAST,
  MIN_TEXT_CONTRAST,
  MIN_UI_CONTRAST
};
